/**
 * The video page, minimal (spec 2026-09-16 §2.1).
 *
 * Ready: the title, what to do next (Open in Studio · Download · Details),
 * the Schedule-post card, the preview beside the post caption — and nothing
 * else. Everything an editor would want lives in the Studio's Edit bar; every
 * verdict, artefact, transcript and log lives behind Details.
 *
 * Rendering: the same header + a compact plain-language progress list — the
 * one place the pipeline shows on the page, because the user is waiting on it.
 *
 * Failed / incomplete: the header, the error, and Recover this clip (the whole
 * EditClipPanel, auto-open on Re-cut) — a job without a video cannot open the
 * Studio, so recovery stays here.
 */
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CalendarClock,
  Check,
  Clapperboard,
  Copy,
  Download,
  Info,
  Loader2,
  MessageSquareQuote,
  Pencil,
  RotateCcw,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import {
  fetchCaption,
  getJob,
  listSchedulingPosts,
  reschedulePost,
  subscribeJob,
  thumbUrl,
  videoUrl,
  type Job,
  type JobListItem,
  type JobStatus,
  type ScheduledPost,
} from "../api";
import {
  extractJobIdFromVideoUrl,
  formatDetailDateTime,
  formatRelative,
  scheduledSummaryForJob,
} from "../lib/scheduledTime";
import { pipelineSourceKind, pipelineStageIndex, pipelineStages } from "../lib/pipelineStages";
import { terminalKind } from "../lib/trackedJobs";
import { ClipDetailsDialog } from "../components/ClipDetailsDialog";
import { JobTitle } from "../components/JobTitle";
import { EditClipPanel } from "../components/editClip/EditClipPanel";
import { RenderHistoryPanel } from "../components/editClip/RenderHistoryPanel";
import {
  displayTitle,
  isTerminalStatus,
  toView,
  type JobView,
  type LiveJobWithArtefacts,
} from "../components/editClip/jobView";
import { RescheduleEditor } from "../components/RescheduleEditor";
import { SchedulePostModal } from "../components/SchedulePostModal";
import { StatusPill } from "../components/StatusPill";
import { ctaGhostStyle, ctaSmallStyle, iconBtnStyle } from "../components/styles";

interface Props {
  jobId: string;
  onBack: () => void;
  /** Navigate to the Studio (iterate editor) screen for this clip. Optional
   *  so JobDetail keeps working from callers that don't wire Studio. */
  onOpenStudio?: () => void;
  /** This clip is the user's FIRST video — the one started from the sample
   *  picker straight after Brand Setup (App resolves it from
   *  `sessionStorage["ql-first-video"]`). Adds the welcome banner that says
   *  what is happening and hands them to the Studio when it lands. */
  firstVideo?: boolean;
  /** Escape hatch when that first video fails: back to New Job on the
   *  sample picker. Absent ⇒ the button is not offered. */
  onTryAnother?: () => void;
  /** Active channel's caption language display name ("Arabic", "French"…).
   *  Names the caption stage in the progress list and the Details dialog.
   *  Absent → "Arabic" (V1 default). */
  captionLanguage?: string;
}

/**
 * The welcome banner on a user's very first render.
 *
 * It answers the only three questions a new user has, in the only three
 * states that matter: it is working and here is how long (nobody waits
 * calmly for an unnamed duration), it is done and here is the one button
 * worth pressing, or it broke and here is the way out that does not require
 * them to find a video of their own.
 */
function FirstVideoBanner({
  status,
  errorMsg,
  onOpenStudio,
  onTryAnother,
}: {
  status: JobStatus;
  errorMsg?: string;
  onOpenStudio?: () => void;
  onTryAnother?: () => void;
}): React.JSX.Element {
  // One classifier for the banner and the render-ready toast, so the two can
  // never disagree about what "finished" means. "incomplete" (an orphaned
  // job — the process died) reads as a failure: same dead end, same way out.
  const kind = terminalKind(status);
  const ready = kind === "ready";
  const failed = kind === "failed";
  const accent = ready ? "74,222,128" : failed ? "248,113,113" : "255,213,0";
  return (
    <div
      // A dead render is an alert, not a status line: it needs to interrupt
      // a screen reader, not wait politely for a gap.
      role={failed ? "alert" : "status"}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "14px 16px",
        borderRadius: 16,
        marginBottom: 20,
        background: `rgba(${accent},.06)`,
        border: `1px solid rgba(${accent},.28)`,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 1, color: `rgb(${accent})` }}>
        {ready ? <Check size={17} strokeWidth={2.4} />
          : failed ? <AlertTriangle size={17} strokeWidth={2} />
            : <Loader2 size={17} className="ql-spin" />}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        {ready ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Ready.</div>
            {onOpenStudio && (
              <button type="button" onClick={onOpenStudio} style={{ ...ctaSmallStyle, marginTop: 10 }}>
                <Clapperboard size={14} strokeWidth={2} />
                Open in Studio →
              </button>
            )}
          </>
        ) : failed ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>That render didn't finish.</div>
            <div
              style={{
                fontSize: 12,
                color: "var(--ql-fg-muted)",
                fontFamily: "var(--ql-font-mono)",
                overflowWrap: "anywhere",
              }}
            >
              {errorMsg || "The pipeline stopped before it produced a video."}
            </div>
            {onTryAnother && (
              <button type="button" onClick={onTryAnother} style={{ ...ctaGhostStyle, marginTop: 10 }}>
                <RotateCcw size={14} strokeWidth={2} />
                Try another sample
              </button>
            )}
          </>
        ) : (
          <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.45 }}>
            Your first video is rendering — usually around ten minutes. When it's ready, click any part of it to
            tweak, or ask the design agent.
          </div>
        )}
      </div>
    </div>
  );
}

export function JobDetail({ jobId, onBack, onOpenStudio, firstVideo, onTryAnother, captionLanguage = "Arabic" }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [view, setView] = useState<JobView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Initial fetch.
  useEffect(() => {
    let cancelled = false;
    setView(null);
    setError(null);
    setDetailsOpen(false);
    void getJob(jobId)
      .then((payload) => {
        if (cancelled) return;
        setView(toView(payload as Job | JobListItem));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  /**
   * Re-read the job after an action started one (recovery retry, a Sync fix
   * from Details). A finished job has no live subscription, so without this
   * the page would sit on the old state while the pipeline ran; once the
   * status flips out of terminal, the subscription below opens and the
   * progress list takes over.
   */
  const refetch = useCallback((): void => {
    void getJob(jobId)
      .then((payload) => setView(toView(payload as Job | JobListItem)))
      .catch(() => { /* the SSE stream, if any, keeps the page honest */ });
  }, [jobId]);

  // Live subscription for in-flight jobs.
  useEffect(() => {
    if (!view) return;
    const isFinished = view.status === "ready" || view.status === "failed";
    if (isFinished) return;

    const es = subscribeJob(jobId, {
      update: (job) =>
        setView((cur) => {
          const next = toView(job);
          // SSE payloads are raw in-memory Jobs without the server-resolved
          // `speaker` — keep the resolved name from the initial fetch instead
          // of letting the guests/host slug guess overwrite it mid-render.
          if (!job.speaker && cur?.speaker) next.speaker = cur.speaker;
          if (!next.title && cur?.title) next.title = cur.title;
          // Same for the artefact flags: an SSE tick knows nothing about what
          // is on disk, so let it dim the Edit actions mid-render and they'd
          // vanish under the operator's cursor. Keep what the fetch resolved.
          const raw = job as LiveJobWithArtefacts;
          if (cur) {
            if (raw.hasVideo === undefined) next.hasVideo = cur.hasVideo || next.hasVideo;
            if (raw.hasCaption === undefined) next.hasCaption = cur.hasCaption;
            if (raw.hasTranscript === undefined) next.hasTranscript = cur.hasTranscript;
          }
          return next;
        }),
      log: (line) =>
        setView((cur) =>
          cur ? { ...cur, logs: [...cur.logs.slice(-499), line] } : cur,
        ),
      error: () => {
        /* ignored — server still keeps file artefacts */
      },
    });
    return () => es.close();
  }, [jobId, view?.status]);

  // Once a render lands, the artefact flags an SSE tick carried are stale —
  // the caption / transcript were written while it ran. Re-read the job from
  // disk so the Post caption card and Details see the new files.
  const status = view?.status;
  useEffect(() => {
    if (status === "ready") refetch();
  }, [status, refetch]);

  // The first-video welcome is a ONE-TIME thing: once the render lands, drop
  // the marker so coming back to this clip tomorrow is the ordinary screen.
  // The banner itself is driven by the PROP, not the key, so it stays put for
  // the rest of this visit — the user still gets their "Open in Studio".
  useEffect(() => {
    if (!firstVideo || view?.status !== "ready") return;
    try { sessionStorage.removeItem("ql-first-video"); } catch { /* private mode */ }
  }, [firstVideo, view?.status]);

  if (error) {
    return (
      <div>
        <BackBar onBack={onBack} title="Video" />
        <div
          style={{
            background: "rgba(248,113,113,.06)",
            border: "1px solid rgba(248,113,113,.30)",
            borderRadius: 14,
            padding: 24,
            color: "var(--ql-danger)",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div>
        <BackBar onBack={onBack} title="Video" />
        <div style={{ padding: 60, textAlign: "center", color: "var(--ql-fg-muted)" }}>Loading…</div>
      </div>
    );
  }

  const title = displayTitle(view);
  const terminal = isTerminalStatus(view.status);
  const inFlight = !terminal;
  // Recovery: a job that stopped (failed / incomplete), or one that says
  // ready but has no file behind it. A video-less job cannot open the Studio.
  const showRecovery = terminal && (view.status !== "ready" || !view.hasVideo);
  const showPreview = terminal && view.hasVideo;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
          flexDirection: isMobile ? "column" : "row",
          marginBottom: 24,
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, width: isMobile ? "100%" : undefined, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <button onClick={onBack} style={iconBtnStyle} title="Back">
            <ArrowLeft size={16} strokeWidth={2} />
          </button>
          <div style={{ minWidth: 0 }}>
            {/* The display title with its inline rename (pencil → input →
                Enter/Esc). A rename lands in the view at once so an SSE
                frame (which carries no title) can't revert the headline —
                the subscription keeps the current title when a frame lacks
                one. The id never headlines the page. */}
            <JobTitle
              jobId={view.id}
              title={title}
              onRenamed={(t) => setView((cur) => (cur ? { ...cur, title: t } : cur))}
              style={{
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                maxWidth: isMobile ? "100%" : 560,
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 6,
                fontSize: 11.5,
                color: "var(--ql-fg-muted)",
              }}
            >
              <StatusPill status={view.status} progress={view.progress} />
              {view.hasVideo && (
                <span style={{ color: "var(--ql-fg-subtle)" }}>
                  rendered {formatRenderedAt(view.createdAt)}
                </span>
              )}
            </div>
            {view.hasVideo && <ScheduledLine jobId={view.id} />}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {view.hasVideo && onOpenStudio && (
            <button
              type="button"
              onClick={onOpenStudio}
              data-tour="open-studio"
              style={ctaSmallStyle}
              title="Fix captions, start & end, sound, camera — and see every change live"
            >
              <Clapperboard size={14} strokeWidth={2} />
              Open in Studio
            </button>
          )}
          {view.hasVideo && (
            <a
              href={videoUrl(view.id)}
              download={`${view.id}-ar.mp4`}
              style={{ ...ctaGhostStyle, textDecoration: "none" }}
            >
              <Download size={14} strokeWidth={2} />
              Download
            </a>
          )}
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            data-tour="details"
            style={{
              ...ctaGhostStyle,
              border: "1px solid rgba(255,255,255,.12)",
              color: "var(--ql-fg-muted)",
              background: "rgba(255,255,255,.02)",
            }}
            title="Sync checks, how it was made, transcript, log, files"
          >
            <Info size={14} strokeWidth={2} />
            Details
          </button>
        </div>
      </div>

      {firstVideo && (
        <FirstVideoBanner
          status={view.status}
          errorMsg={view.errorMsg}
          onOpenStudio={view.hasVideo ? onOpenStudio : undefined}
          onTryAnother={onTryAnother}
        />
      )}

      {view.errorMsg && (
        <div
          style={{
            background: "rgba(248,113,113,.06)",
            border: "1px solid rgba(248,113,113,.30)",
            borderRadius: 14,
            padding: 16,
            color: "var(--ql-danger)",
            fontSize: 13,
            marginBottom: 20,
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          {view.errorMsg}
        </div>
      )}

      {inFlight && <ProgressList view={view} captionLanguage={captionLanguage} />}

      {showRecovery && (
        <EditClipPanel
          jobId={view.id}
          status={view.status}
          hasVideo={view.hasVideo}
          hasTranscript={view.hasTranscript}
          originalFrom={view.input?.from}
          originalTo={view.input?.to}
          originalDuration={view.input?.duration}
          originalPhrase={view.input?.startPhrase}
          originalEndPhrase={view.input?.endPhrase}
          originalMusicTrack={view.input?.musicTrack}
          onJobStarted={refetch}
        />
      )}

      {/* Recovery stays on the page (spec §3): every earlier version of a
          clip without a video is restorable right here, not only the latest
          one the panel offers. */}
      {showRecovery && (
        <div style={{ marginBottom: 20 }}>
          <RenderHistoryPanel jobId={view.id} status={view.status} onRestored={refetch} />
        </div>
      )}

      {showPreview && view.status === "ready" && (
        <SchedulePostCTA jobId={view.id} />
      )}

      {showPreview && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 360px) minmax(0, 1fr)",
            gap: 20,
            alignItems: "start",
            marginTop: 20,
          }}
        >
          <PreviewBox view={view} />
          <PostCaptionCard jobId={view.id} hasCaption={view.hasCaption} />
        </div>
      )}

      {detailsOpen && (
        <ClipDetailsDialog
          view={view}
          captionLanguage={captionLanguage}
          onClose={() => setDetailsOpen(false)}
          onJobStarted={() => { setDetailsOpen(false); refetch(); }}
        />
      )}
    </div>
  );
}

/**
 * While the pipeline runs: the plain-language stages with a check on what
 * ran, a spinner on what is running, and the rest muted. The only place the
 * pipeline shows on the page.
 */
function ProgressList({ view, captionLanguage }: { view: JobView; captionLanguage: string }): React.JSX.Element {
  const stages = useMemo(
    () => pipelineStages(captionLanguage, pipelineSourceKind(view.input, view.sourceUrl)),
    [captionLanguage, view.input, view.sourceUrl],
  );
  const idx = pipelineStageIndex(stages, view.status, view.currentStage);
  return (
    <div
      role="status"
      style={{
        background: "rgba(16,18,27,.72)",
        borderRadius: 20,
        border: "1px solid rgba(255,255,255,.06)",
        padding: 18,
        marginBottom: 20,
        maxWidth: 520,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ql-yellow)", textTransform: "uppercase", letterSpacing: ".08em" }}>
          Building your video
        </div>
        <span style={{ fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)" }}>
          {Math.round(view.progress * 100)}%
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {stages.map((s, i) => {
          const done = i < idx;
          const running = i === idx;
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 4px" }}>
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: done ? "rgba(74,222,128,.14)" : running ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.04)",
                  color: done ? "#4ADE80" : running ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
                  border: running ? "1px solid rgba(255,213,0,.40)" : "1px solid transparent",
                  boxShadow: running ? "0 0 18px rgba(255,213,0,.25)" : "none",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {done ? <Check size={13} strokeWidth={2.5} /> : running ? <Loader2 size={13} className="ql-spin" /> : i + 1}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: done ? "var(--ql-gray-200)" : running ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
                }}
              >
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatRenderedAt(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  if (sameDay) return `today at ${hh}:${mm}`;
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yest.getFullYear() &&
    d.getMonth() === yest.getMonth() &&
    d.getDate() === yest.getDate();
  if (isYesterday) return `yesterday at ${hh}:${mm}`;
  return `${d.getMonth() + 1}/${d.getDate()} at ${hh}:${mm}`;
}

function BackBar({ onBack, title }: { onBack: () => void; title: string }): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
      <button onClick={onBack} style={iconBtnStyle} title="Back">
        <ArrowLeft size={16} strokeWidth={2} />
      </button>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: "var(--ql-fg-muted)",
        }}
      >
        {title}
      </div>
    </div>
  );
}

/** The 9:16 preview — the same box the page always had, without the panels
 *  that used to hang under it (those live in the Studio and in Details now). */
function PreviewBox({ view }: { view: JobView }): React.JSX.Element {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        background: "rgba(16,18,27,.72)",
        borderRadius: 20,
        padding: 16,
        border: "1px solid rgba(255,255,255,.06)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          Preview · 9:16
        </div>
        {view.durationSec ? (
          <span style={{ fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)" }}>
            {Math.round(view.durationSec)}s
          </span>
        ) : null}
      </div>

      <div
        style={{
          aspectRatio: "9/16",
          borderRadius: 14,
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg,#1a1f2e 0%,#0a0a0b 60%,#1f1815 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.04)",
          maxWidth: isMobile ? 280 : "100%",
          width: "100%",
          alignSelf: "center",
        }}
      >
        <video
          controls
          preload="metadata"
          poster={thumbUrl(view.id)}
          style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }}
          src={videoUrl(view.id)}
        />
      </div>
    </div>
  );
}

/**
 * The post caption beside the preview: the text ready to paste, with Copy.
 * Writing it again lives in the Studio's Edit bar (Captions & text).
 */
function PostCaptionCard({ jobId, hasCaption }: { jobId: string; hasCaption: boolean }): React.JSX.Element {
  const [text, setText] = useState<string | null | undefined>(undefined);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchCaption(jobId).then((data) => {
      if (!cancelled) setText(data);
    });
    return () => {
      cancelled = true;
    };
  }, [jobId, hasCaption]);

  const handleCopy = async (): Promise<void> => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignored */
    }
  };

  return (
    <div
      style={{
        background: "rgba(16,18,27,.72)",
        borderRadius: 20,
        border: "1px solid rgba(255,255,255,.06)",
        padding: 18,
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          <MessageSquareQuote size={13} strokeWidth={2} />
          Post caption
        </span>
        {text && (
          <button
            type="button"
            onClick={handleCopy}
            style={{
              background: "rgba(255,213,0,.14)",
              border: "1px solid rgba(255,213,0,.30)",
              color: "var(--ql-yellow)",
              padding: "4px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
            }}
          >
            {copied ? <Check size={12} strokeWidth={2.5} /> : <Copy size={12} strokeWidth={2} />}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
      {text === undefined ? (
        <div style={{ fontSize: 13, color: "var(--ql-fg-muted)" }}>Loading…</div>
      ) : text === null ? (
        <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
          Post caption is written after the video is built.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginBottom: 10 }}>
            Ready to paste · Instagram · TikTok · Reels
          </div>
          <div
            style={{
              padding: 16,
              borderRadius: 14,
              background: "rgba(255,255,255,.02)",
              border: "1px solid rgba(255,255,255,.06)",
              whiteSpace: "pre-wrap",
              fontSize: 14,
              lineHeight: 1.65,
              color: "var(--ql-gray-200)",
              fontFamily: "var(--ql-font-sans)",
              overflowWrap: "anywhere",
            }}
          >
            {text}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Header line showing *when* a finished clip publishes, for clips that are
 * already scheduled. Self-fetching (mirrors SchedulePostCTA) — reuses the
 * provider-agnostic `listSchedulingPosts()` and the shared
 * `scheduledSummaryForJob` resolver rather than coupling the job payload to
 * the scheduler. Renders nothing until the lookup resolves to a scheduled
 * post for this job, so an unscheduled or in-flight clip shows no line.
 */
function ScheduledLine({ jobId }: { jobId: string }): React.JSX.Element | null {
  const [posts, setPosts] = useState<ScheduledPost[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    listSchedulingPosts()
      .then((res) => {
        if (!cancelled) setPosts(res.posts);
      })
      .catch(() => {
        /* best-effort — no scheduler connected / throttled → no line */
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);
  const refetch = (): void => {
    listSchedulingPosts()
      .then((res) => setPosts(res.posts))
      .catch(() => {});
  };

  const summary = posts ? scheduledSummaryForJob(posts, jobId) : null;
  if (!summary || !posts) return null;

  // Every channel this video is queued on — moved together on reschedule.
  const jobPosts = posts.filter(
    (p) => p.status === "scheduled" && extractJobIdFromVideoUrl(p.videoUrl) === jobId && !!p.dueAt,
  );
  const extra = summary.count - 1;

  return (
    <div style={{ marginTop: 8, display: "grid", gap: 8, maxWidth: 440 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, flexWrap: "wrap" }}>
        <CalendarClock size={13} strokeWidth={2} style={{ flexShrink: 0, color: "var(--ql-yellow)" }} />
        <span style={{ fontWeight: 700, color: "var(--ql-yellow)" }}>Scheduled</span>
        <span style={{ color: "var(--ql-fg-muted)" }}>
          {formatDetailDateTime(summary.at)}
          {" · "}
          {formatRelative(summary.at)}
        </span>
        {extra > 0 && (
          <span style={{ color: "var(--ql-fg-subtle)" }}>
            · +{extra} more channel{extra === 1 ? "" : "s"}
          </span>
        )}
        {jobPosts.length > 0 && (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            title="Change the date & time"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 9px",
              borderRadius: 7,
              border: editing ? "1px solid rgba(255,213,0,.5)" : "1px solid rgba(255,255,255,.14)",
              background: editing ? "rgba(255,213,0,.10)" : "transparent",
              color: editing ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <Pencil size={11} strokeWidth={2} /> Edit time
          </button>
        )}
      </div>
      {editing && jobPosts.length > 0 && (
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid rgba(255,213,0,.22)",
            background: "rgba(255,213,0,.04)",
            display: "grid",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ql-fg)" }}>Change date &amp; time</div>
          <RescheduleEditor
            currentISO={summary.at.toISOString()}
            onSave={async (iso) => {
              // Fan out to every channel; a single channel failing must NOT
              // discard the ones that moved (mirrors the post-draft routes).
              const results = await Promise.allSettled(jobPosts.map((p) => reschedulePost(p.id, iso)));
              const failed = results.filter(
                (r): r is PromiseRejectedResult => r.status === "rejected",
              );
              if (failed.length === jobPosts.length) {
                // Nothing moved → surface the error and keep the editor open.
                const reason = failed[0]?.reason;
                throw reason instanceof Error ? reason : new Error(String(reason));
              }
              setEditing(false);
              setWarn(
                failed.length > 0
                  ? `${failed.length} of ${jobPosts.length} channels didn't move — the rest were rescheduled.`
                  : null,
              );
              refetch();
            }}
            onCancel={() => setEditing(false)}
            saveLabel="Save time"
            note={
              jobPosts.length > 1
                ? `Moves all ${jobPosts.length} channels of this video together.`
                : undefined
            }
          />
        </div>
      )}
      {warn && (
        <div style={{ fontSize: 11.5, color: "#f2c94c", lineHeight: 1.4 }}>{warn}</div>
      )}
    </div>
  );
}

/**
 * "Schedule post" CTA shown after a successful render. Lazily fetches the
 * generated social caption from the API and hands it to the modal as the
 * prefilled default text. Keeps the CTA self-contained so JobDetail
 * doesn't have to thread schedule state through its tree.
 */
function SchedulePostCTA({ jobId }: { jobId: string }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState<string>("");
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    void fetchCaption(jobId).then((c) => {
      if (c) setCaption(c);
    });
  }, [jobId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div
      style={{
        marginTop: 12,
        padding: "12px 16px",
        background: "rgba(255,213,0,.06)",
        border: "1px solid rgba(255,213,0,.20)",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Calendar size={16} strokeWidth={1.75} color="var(--ql-yellow)" />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ql-fg)" }}>Ready to publish?</div>
          <div style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>
            Schedule this video across all your Buffer channels — per-platform captions handled automatically.
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {toast && (
          <span style={{ fontSize: 11, color: "#86EFAC", maxWidth: 320 }}>{toast}</span>
        )}
        <button
          onClick={() => setOpen(true)}
          data-tour="schedule-post"
          style={{
            ...ctaSmallStyle,
            cursor: "pointer",
          }}
        >
          <Calendar size={13} strokeWidth={2} />
          Schedule post
        </button>
      </div>

      <SchedulePostModal
        open={open}
        jobId={jobId}
        defaultCaption={caption}
        onClose={() => setOpen(false)}
        onScheduled={(when, count) =>
          setToast(`Queued ${count} post${count === 1 ? "" : "s"} for ${new Date(when).toLocaleString()}`)
        }
      />
    </div>
  );
}
